import React, { useEffect, useRef } from 'react';

const VideoPlayer = ({
  stream,
  screenStream = null,
  userId,
  nickname,
  isLocal = false,
  isScreenShare = false,
  isPinned = false,
  isSpeaking = false,
  audioEnabled = true,
  videoEnabled = true,
  networkStrength = 'good',
  onPinUser = null,
  isSmall = false,
  fit = 'contain', // Default to contain for better face preservation
  className = '',
}) => {
  const videoRef = useRef(null);
  const audioRef = useRef(null);

  const shouldShowScreenShare = isScreenShare && !!screenStream;
  const shouldShowVideo = !isScreenShare && !!stream && videoEnabled;
  const isAudioOnly = !isScreenShare && !!stream && audioEnabled && !videoEnabled;
  const shouldShowAvatar = !isScreenShare && (!stream || !videoEnabled);

  // Attach stream to <video> or <audio>
  useEffect(() => {
    // Screen share or normal video
    if (shouldShowScreenShare || shouldShowVideo) {
      if (!videoRef.current) return;
      const mediaStream = shouldShowScreenShare ? screenStream : stream;

      if (videoRef.current.srcObject !== mediaStream) {
        videoRef.current.srcObject = mediaStream || null;
        if (mediaStream) {
          console.log("Video pixels: ",videoRef.current.srcObject.getVideoTracks()[0].getSettings()); // Log video track settings
        }
      }

      if (mediaStream) {
        videoRef.current
          .play()
          .catch(err => console.warn('Autoplay blocked for video:', err));
      }

      // Make sure audio element is cleared in this mode
      if (audioRef.current && audioRef.current.srcObject) {
        audioRef.current.srcObject = null;
      }
      return;
    }

    // Audio-only case
    if (isAudioOnly) {
      if (!audioRef.current) return;

      if (audioRef.current.srcObject !== stream) {
        audioRef.current.srcObject = stream || null;
      }

      if (stream) {
        audioRef.current
          .play()
          .catch(err => console.warn('Autoplay blocked for audio:', err));
      }

      // Clear video element when not used
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject = null;
      }
      return;
    }

    // No media: clear both
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject = null;
    }
    if (audioRef.current && audioRef.current.srcObject) {
      audioRef.current.srcObject = null;
    }
  }, [stream, screenStream, shouldShowScreenShare, shouldShowVideo, isAudioOnly]);

  const handlePinClick = () => {
    if (onPinUser) onPinUser(userId);
  };

  const getNetworkStrengthClass = () => {
    switch (networkStrength) {
      case 'excellent': return 'excellent';
      case 'good': return 'good';
      case 'poor': return 'poor';
      case 'bad': return 'bad';
      default: return 'good';
    }
  };

  const getAvatarColor = (name) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    ];
    const hash = name.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div
      className={
        `video-player ${isPinned ? 'pinned' : ''} ` +
        `${isSpeaking ? 'speaking' : ''} ` +
        `${isScreenShare ? 'screen-share' : ''} ` +
        `${isSmall ? 'small' : ''} ${className}`
      }
    >
      {/* Visible media area */}
      {shouldShowScreenShare && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="video-element screen-share-video"
          style={{ objectFit: fit }}
        />
      )}

      {!shouldShowScreenShare && shouldShowVideo && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className="video-element"
          style={{ objectFit: fit }}
        />
      )}

      {/* Audio-only playback (hidden) */}
      {isAudioOnly && (
        <audio
          ref={audioRef}
          autoPlay
          muted={isLocal}
          style={{ display: 'none' }}
        />
      )}

      {/* Avatar when camera is off or no stream */}
      {shouldShowAvatar && (
        <div
          className="video-avatar"
          style={{ background: getAvatarColor(nickname) }}
        >
          <div className="avatar-initial">
            {nickname.charAt(0).toUpperCase()}
          </div>
          <div className="avatar-pulse"></div>
        </div>
      )}

      <div className="video-info">
        <div className="user-name">
          {nickname} {isLocal && '(You)'} {isScreenShare && '(Screen)'}
        </div>
        <div className="media-status">
          {!audioEnabled && <div className="status-muted">🔇</div>}
          {shouldShowAvatar && <div className="status-muted">📷</div>}
          <div className="connection-quality">
            <div className={`quality-bar ${getNetworkStrengthClass()}`} />
            <div className={`quality-bar ${getNetworkStrengthClass()}`} />
            <div className={`quality-bar ${getNetworkStrengthClass()}`} />
          </div>
        </div>
      </div>

      {!isLocal && onPinUser && (
        <button onClick={handlePinClick} className="pin-button">
          {isPinned ? 'Unpin' : 'Pin'}
        </button>
      )}

      {isScreenShare && (
        <div className="screen-share-indicator">
          {isLocal ? 'Your Screen' : `${nickname}'s Screen`}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
